import ProductIcon from '@/components/ProductIcon'
import useSeti from '@/hooks/useSeti'
import type { ProductType } from '@/types'
import { motion } from 'motion/react'
import { Box, Button, Flex, Text, getPaletteColor } from 'pcln-design-system'
import { useCallback, useState } from 'react'
import styled from 'styled-components'
import { PRODUCT_TABS, PRODUCT_TABS_AI_MODE } from './constants'

const Wrapper = styled(Flex)`
  position: relative;
`
const IconWrapper = styled(Flex)<{ isActive: boolean }>`
  background: ${(props) => (props.isActive ? 'transparent' : getPaletteColor('background.base'))};
`

const TabWithIcon = styled(Button)<{ isActive: boolean }>`
  &:hover {
    text-decoration: none;
  }
  z-index: 1;
  position: relative;
  &:hover ${IconWrapper} {
    border: 1px solid ${(props) => (props.isActive ? 'transparent' : getPaletteColor('primary.base'))};
  }
`

const Slider = styled(Box)`
  top: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  position: absolute;
  border: 2px solid ${getPaletteColor('primary.base')};
  background-color: ${getPaletteColor('primary.light')};
`
const MotionSlider = motion.create(Slider)

export default function Tabs({
  initialActiveTab,
  onTabClick,
}: Readonly<{
  initialActiveTab: ProductType
  onTabClick: (name: ProductType) => void
}>) {
  const [activeTab, setActiveTab] = useState(initialActiveTab)
  const [hoveredTab, setHoveredTab] = useState('')

  const useAITab = useSeti({ experimentCode: 'USP_AI_SEARCH_TAB_HP' }) === 'VARIANT_TAB'

  const productTabs = useAITab ? PRODUCT_TABS_AI_MODE : PRODUCT_TABS

  const desktopMargin = useAITab ? 1 : 3

  const handleTabClick = useCallback(
    (tabId: ProductType) => {
      if (tabId !== 'ai_mode') {
        setActiveTab(tabId)
      }
      onTabClick(tabId)
    },
    [onTabClick],
  )

  return (
    <Wrapper role='tablist' aria-label='Types of travel' mx={3} ml={1} mb={3}>
      {productTabs.map((tab) => {
        const isActive = tab.id === activeTab
        const isHoveredTab = tab.id === hoveredTab

        return (
          <TabWithIcon
            mr={[0, null, null, desktopMargin]}
            width={[1, null, null, 'auto']}
            key={tab.id}
            color='primary'
            variation='link'
            role='tab'
            pr={[2, null, null, 3]}
            borderRadius='full'
            isActive={isActive}
            aria-selected={isActive}
            aria-label={`Search for ${tab.id}`}
            data-testid={tab.autobotID}
            onMouseEnter={() => setHoveredTab(tab.id)}
            onMouseLeave={() => setHoveredTab('')}
            onClick={() => handleTabClick(tab.id)}
          >
            {isActive && (
              <MotionSlider borderRadius='full' layoutId='bubble' transition={{ duration: 0.4 }} />
            )}
            <Flex alignItems='center' justifyContent='center' style={{ border: '2px solid transparent' }}>
              <Box display={['none', null, 'block']}>
                <IconWrapper
                  isActive={isActive}
                  alignItems='center'
                  justifyContent='center'
                  pt='2px'
                  borderRadius='50%'
                  width={40}
                  height={40}
                >
                  <ProductIcon name={tab.id} isActive={isActive || isHoveredTab} size='24px' />
                </IconWrapper>
              </Box>
              <Text
                ml={2}
                mb='2px'
                fontSize={1}
                fontWeight={isActive ? 'bold' : 100}
                style={{ whiteSpace: 'nowrap' }}
              >
                {tab.tabText}
              </Text>
            </Flex>
          </TabWithIcon>
        )
      })}
    </Wrapper>
  )
}
